import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const TikTokUGCAds = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>TikTok UGC Ads | Instagram Reels UGC | Short Form Video Ads | Fuel72</title>
        <meta
          name="description"
          content="Create TikTok UGC ads and Instagram Reels content with AI. Professional short-form video ads optimized for viral performance. 72-hour delivery."
        />
        <meta
          name="keywords"
          content="tiktok ugc ads, instagram reels ugc, short form ads, tiktok ad creatives, reels advertising, vertical video ads, tiktok marketing, ugc for tiktok"
        />
        <link rel="canonical" href="https://www.fuel72.com/tiktok-ugc-ads" />
        
        {/* Hreflang */}
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/tiktok-ugc-ads" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-werbevideo" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/tiktok-ugc-ads" />
        
        {/* Open Graph */}
        <meta property="og:title" content="TikTok UGC Ads | Instagram Reels | Fuel72" />
        <meta property="og:description" content="AI-generated short-form video ads for TikTok and Instagram Reels. Go viral." />
        <meta property="og:url" content="https://www.fuel72.com/tiktok-ugc-ads" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fuel72.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TikTok UGC Ads | Fuel72" />
        <meta name="twitter:description" content="Create viral TikTok and Reels content with AI. No filming needed." />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "TikTok UGC Ad Creation",
            "name": "TikTok & Instagram Reels UGC Ads",
            "description": "Create viral short-form video ads for TikTok and Instagram Reels with AI. Optimized for engagement.",
            "provider": { "@type": "Organization", "name": "Fuel72" },
            "areaServed": ["US", "CA", "AU", "GB", "DE", "Worldwide"]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What makes a good TikTok UGC ad?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Great TikTok UGC ads feel authentic, use trending formats, hook viewers in the first 3 seconds, and have a clear call-to-action. Fuel72 creates all of this with AI."
                }
              },
              {
                "@type": "Question",
                "name": "Can AI create TikTok-style content?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Fuel72's AI creates authentic TikTok-style UGC that feels native to the platform. Optimized for the algorithm and designed to engage."
                }
              },
              {
                "@type": "Question",
                "name": "What video format is best for TikTok ads?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "TikTok ads should be 9:16 vertical, 15-60 seconds long, with captions. Fuel72 delivers videos in all optimal formats for TikTok and Reels."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen gradient-mesh flex items-center justify-center">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            TikTok & Reels{" "}
            <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
              UGC Ads
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Create viral short-form video ads with AI. 
            Optimized for TikTok, Instagram Reels, and YouTube Shorts.
          </p>
          
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <h2 className="text-2xl font-bold">Why Short-Form UGC Dominates</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>🚀 <strong>Highest Engagement</strong> - Short videos get 2.5x more engagement</li>
              <li>📱 <strong>Mobile-First</strong> - Designed for how people consume content</li>
              <li>🎯 <strong>Algorithm Boost</strong> - Platforms prioritize short-form content</li>
              <li>💸 <strong>Lower Ad Costs</strong> - Better performance = lower CPM</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8">Platforms We Optimize For</h2>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="p-6 rounded-lg bg-card/50 text-center">
                <span className="text-4xl">🎵</span>
                <p className="font-bold mt-2">TikTok</p>
                <p className="text-xs text-muted-foreground">9:16 vertical</p>
              </div>
              <div className="p-6 rounded-lg bg-card/50 text-center">
                <span className="text-4xl">📸</span>
                <p className="font-bold mt-2">Reels</p>
                <p className="text-xs text-muted-foreground">Instagram & Facebook</p>
              </div>
              <div className="p-6 rounded-lg bg-card/50 text-center">
                <span className="text-4xl">▶️</span>
                <p className="font-bold mt-2">Shorts</p>
                <p className="text-xs text-muted-foreground">YouTube</p>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mt-8">What You Get</h2>
            <p className="text-muted-foreground">
              6 TikTok-optimized UGC videos, vertical format (9:16), with captions, 
              trending hooks, and clear CTAs. Perfect for paid ads or organic content.
            </p>
          </div>
          
          <p className="text-sm text-muted-foreground mt-12">Redirecting to homepage...</p>
        </div>
      </main>
    </>
  );
};

export default TikTokUGCAds;
